import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Plus, Search, List, LayoutGrid, GripVertical, 
  Edit3, Trash2, Eye, EyeOff, Save, X, 
  CheckCircle2, AlertCircle, Star, Info, Layout,
  Camera, FileText, Settings, ChevronRight, RefreshCcw
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchServicesAdmin, createService, updateService, deleteService } from '../../lib/services';
import { getFileUrl, pb } from '../../lib/pb';

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [draggingId, setDraggingId] = useState(null);
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchServicesAdmin(page, 50, search);
      setItems(res.items || []);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, search]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('services').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('services').unsubscribe('*'); };
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter(s => {
      if (filterStatus === 'published' && !s.published) return false;
      if (filterStatus === 'draft' && s.published) return false;
      return true;
    });
  }, [items, filterStatus]);

  const handleDragStart = (id) => setDraggingId(id);

  const handleDragOver = (targetId, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(s => s.id === draggingId);
      const toIdx = next.findIndex(s => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const persistOrder = async () => {
    if (!items.length) return;
    try {
      const updates = items.map((s, index) => {
        const newOrder = (index + 1) * 10;
        if (s.order !== newOrder) return updateService(s.id, { order: newOrder });
        return null;
      }).filter(Boolean);
      if (updates.length) {
        await Promise.all(updates);
        setSuccess('Ordre mis à jour ✨');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError("Erreur de réorganisation"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    persistOrder();
    setDraggingId(null);
  };

  const handleTogglePublished = async (s) => {
    // Mise à jour optimiste immédiate
    const oldStatus = s.published;
    setItems(prev => prev.map(item => item.id === s.id ? { ...item, published: !oldStatus } : item));

    try {
      await updateService(s.id, { published: !oldStatus });
      setSuccess(!oldStatus ? 'Service publié ⭐' : 'Service dépublié 🌑');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      // Rollback en cas d'erreur
      setItems(prev => prev.map(item => item.id === s.id ? { ...item, published: oldStatus } : item));
      setError(e.message);
    }
  };

  const openCreate = () => {
    setMode('create');
    setEditing({ title: '', slug: '', excerpt: '', description: '', features: [], icon: '', published: true });
  };

  const openEdit = (s) => { setMode('edit'); setEditing(s); };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le service "${s.title}" ?`)) return;
    try {
      await deleteService(s.id);
      setSuccess('Service supprimé 🗑️');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Catalogue</div>
          <h1>Nos Services <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Gérez vos prestations et l'ordre d'affichage sur le portail public.</p>
        </div>
        <button className="btn-premium-gold" onClick={openCreate}>
          <Plus size={20} />
          <span>Ajouter un service</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="cockpit-filters-bar shadow-signature">
        <div className="search-pill">
          <Search size={18} />
          <input 
            placeholder="Rechercher une prestation..." 
            value={search} 
            onChange={e => { setPage(1); setSearch(e.target.value); }}
          />
        </div>

        <div className="filters-and-view">
          <div className="filter-selects">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="all">Tous les statuts</option>
                <option value="published">Publiés</option>
                <option value="draft">Brouillons</option>
            </select>
          </div>

          <div className="view-selector">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Chargement du catalogue...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <Star size={48} />
          <h3>Aucun service trouvé</h3>
          <p>Commencez par ajouter votre première prestation pour l'afficher sur le site.</p>
          <button className="btn filled" onClick={openCreate}>Créer un service</button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`services-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {filteredItems.map((s) => (
                <div 
                  key={s.id} 
                  className={`list-item-row ${draggingId === s.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(s.id)}
                  onDragOver={(e) => handleDragOver(s.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="drag-handle"><GripVertical size={18}/></div>
                  <div className="item-icon-box">
                    {s.icon ? (
                      <img src={getFileUrl(s, s.icon)} alt={s.title} />
                    ) : (
                      <Star size={20} className="placeholder" />
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-title">{s.title}</div>
                    <div className="item-meta">
                      {s.excerpt ? s.excerpt : "Aucun résumé"}
                    </div>
                  </div>
                  <div className="item-status">
                     <button className={`status-pill ${s.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(s)}>
                        {s.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                        <span>{s.published ? 'En ligne' : 'Masqué'}</span>
                     </button>
                  </div>
                  <div className="item-actions">
                    <button className="action-btn edit" onClick={() => openEdit(s)} title="Modifier">
                      <Edit3 size={18}/>
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(s)} title="Supprimer">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="premium-grid">
              {filteredItems.map((s) => (
                <div 
                  key={s.id} 
                  className="service-card-premium animation-slide-up"
                  draggable
                  onDragStart={() => handleDragStart(s.id)}
                  onDragOver={(e) => handleDragOver(s.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="card-media">
                    {s.icon ? <img src={getFileUrl(s, s.icon)} alt={s.title} /> : <div className="card-placeholder"><Star size={40}/></div>}
                    <div className="card-overlay">
                       <button className="overlay-edit" onClick={() => openEdit(s)}><Edit3 size={20}/></button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                       <div className="card-tag">Prestation</div>
                       <div className="card-status-dot" style={{background: s.published ? '#10b981' : '#cbd5e1'}}></div>
                    </div>
                    <h3 className="card-title">{s.title}</h3>
                    <p className="card-excerpt">{s.excerpt}</p>
                    <div className="card-actions-row">
                       <button className={`btn-mini ${s.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(s)} title={s.published ? 'Masquer' : 'Publier'}>
                          {s.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                       </button>
                       <div className="spacer"></div>
                       <button className="btn-mini edit" onClick={() => openEdit(s)} title="Modifier"><Edit3 size={16}/></button>
                       <button className="btn-mini delete" onClick={() => handleDelete(s)} title="Supprimer"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <ServiceStudio
          mode={mode}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .services-content-container.grid { display: block; }
        .premium-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 25px; padding: 20px 0; }
        
        .service-card-premium { background: #fff; border-radius: 28px; border: 1px solid var(--admin-border); overflow: hidden; position: relative; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: grab; }
        .service-card-premium:hover { transform: translateY(-8px); box-shadow: 0 20px 40px -12px rgba(0,0,0,0.1); border-color: var(--admin-primary); }
        
        .card-media { height: 180px; position: relative; background: #f8fafc; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .card-media img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
        .service-card-premium:hover .card-media img { transform: scale(1.1); }
        .card-placeholder { color: #cbd5e1; }
        
        .card-overlay { position: absolute; inset: 0; background: rgba(15, 23, 42, 0.4); opacity: 0; display: flex; align-items: center; justify-content: center; transition: all 0.3s; backdrop-filter: blur(4px); }
        .service-card-premium:hover .card-overlay { opacity: 1; }
        .overlay-edit { width: 50px; height: 50px; border-radius: 50%; background: #fff; border: none; color: #0f172a; cursor: pointer; display: flex; align-items: center; justify-content: center; transform: scale(0.8); transition: all 0.2s; }
        .overlay-edit:hover { background: var(--admin-primary); transform: scale(1); }

        .card-body { padding: 24px; }
        .card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .card-tag { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; font-weight: 900; color: #94a3b8; }
        .card-status-dot { width: 8px; height: 8px; border-radius: 50%; }
        
        .card-title { font-size: 18px; font-weight: 800; margin: 0 0 10px 0; color: #0f172a; }
        .card-excerpt { font-size: 13px; color: #64748b; line-height: 1.5; margin: 0 0 20px 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; height: 40px; }
        
        .card-footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 18px; }
        .btn-card-del { width: 36px; height: 36px; border-radius: 12px; border: none; background: #fff1f2; color: #f43f5e; cursor: pointer; transition: all 0.2s; }
        .btn-card-del:hover { background: #fee2e2; transform: rotate(-10deg); }
        .btn-card-edit { background: transparent; border: none; color: var(--admin-primary); font-weight: 900; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 5px; }

        .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 100px 0; color: #64748b; }
        .spin { animation: spin 1s linear infinite; margin-bottom: 15px; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .toast-success { position: fixed; bottom: 30px; right: 30px; background: #10b981; color: #fff; padding: 15px 25px; border-radius: 16px; display: flex; align-items: center; gap: 12px; font-weight: 800; box-shadow: 0 20px 25px -5px rgba(16, 185, 129, 0.3); z-index: 1000; animation: slideInRight 0.3s ease; }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }

        @media (max-width: 768px) {
          .premium-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

function ServiceStudio({ mode, initial, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('general');
  const [title, setTitle] = useState(initial.title || '');
  const [slug, setSlug] = useState(initial.slug || '');
  const [excerpt, setExcerpt] = useState(initial.excerpt || '');
  const [description, setDescription] = useState(initial.description || '');
  const [featuresText, setFeaturesText] = useState(() => {
    if (Array.isArray(initial.features)) return initial.features.join('\n');
    return '';
  });
  const [published, setPublished] = useState(initial.published ?? true);
  const [icon, setIcon] = useState(() => initial.icon ? { type: 'existing', name: initial.icon } : null);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 900, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression error')), 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image error"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleIconChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setIcon({ type: 'new', file: finalFile });
    } catch (err) { setError("Erreur compression image"); }
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    if (mode === 'create' && !slug) {
      setSlug(value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const features = featuresText.split('\n').map(l => l.trim()).filter(Boolean);
    const data = { title, slug, excerpt, description, features, published };

    if (icon) { if (icon.type === 'new') data.icon = icon.file; } 
    else { data.icon = null; }

    try {
      if (mode === 'create') await createService(data);
      else await updateService(initial.id, data);
      onSaved();
    } catch (err) { setError(err?.message || 'Erreur sauvegarde'); } 
    finally { setSaving(false); }
  };

  const previewUrl = icon ? (icon.type === 'existing' ? getFileUrl(initial, icon.name) : URL.createObjectURL(icon.file)) : '';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container studio-service animation-slide-up">
        <header className="modal-header-premium">
          <div className="header-info">
            <div className="header-tag">{mode === 'create' ? 'Nouveau' : 'Édition'}</div>
            <h2>Studio Service</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
        </header>

        <div className="modal-tabs">
          <button className={activeTab === 'general' ? 'active' : ''} onClick={() => setActiveTab('general')}>
            <Layout size={16}/> <span>Général</span>
          </button>
          <button className={activeTab === 'visual' ? 'active' : ''} onClick={() => setActiveTab('visual')}>
            <Camera size={16}/> <span>Visuel</span>
          </button>
          <button className={activeTab === 'content' ? 'active' : ''} onClick={() => setActiveTab('content')}>
            <FileText size={16}/> <span>Description</span>
          </button>
        </div>

        <form onSubmit={onSubmit} className="modal-body-premium">
           {error && <div className="modal-error"><AlertCircle size={14}/> {error}</div>}

           <div className="tab-panels">
              {activeTab === 'general' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="form-group-premium">
                      <label>Désignation du service</label>
                      <input value={title} onChange={handleTitleChange} required placeholder="ex: Isolation des combles" />
                   </div>
                   <div className="form-group-premium">
                      <label>Index (Slug)</label>
                      <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="isolation-combles" />
                      <p className="field-hint">Utilisé pour l'URL de la page spécifique.</p>
                   </div>
                   <div className="form-group-premium">
                      <label>Résumé accrocheur</label>
                      <textarea rows={3} value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Courte introduction pour les cartes..." />
                   </div>
                   <div className="toggle-switch-group">
                      <span>Rendre ce service public sur le site</span>
                      <label className="switch">
                         <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                         <span className="slider round"></span>
                      </label>
                   </div>
                </div>
              )}

              {activeTab === 'visual' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="visual-studio">
                      <div className="preview-main-box service">
                         {previewUrl ? <img src={previewUrl} alt="Preview" /> : <div className="no-image"><Star size={48}/></div>}
                      </div>
                      <div className="visual-actions">
                         <label className="btn-upload-premium">
                            <Camera size={18}/> <span>Choisir une icône / photo</span>
                            <input type="file" hidden accept="image/*" onChange={handleIconChange} />
                         </label>
                         {icon && <button type="button" className="btn-remove-media" onClick={() => setIcon(null)}><Trash2 size={16}/> Supprimer</button>}
                         <div className="visual-notes">
                            <Info size={14}/> <span>Utilisez une icône épurée ou une photo d'illustration haute résolution.</span>
                         </div>
                      </div>
                   </div>
                </div>
              )}

              {activeTab === 'content' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="form-group-premium">
                      <label>Description exhaustive</label>
                      <textarea rows={6} value={description} onChange={e => setDescription(e.target.value)} placeholder="Expliquez en détail votre expertise et votre approche..." />
                   </div>
                   <div className="form-group-premium">
                      <label>Arguments clés (Un par ligne)</label>
                      <textarea rows={4} className="features-area" value={featuresText} onChange={e => setFeaturesText(e.target.value)} placeholder={"Garantie décennale\nMatériaux certifiés RGE\nNettoyage de chantier inclus"} />
                      <p className="field-hint">Ces points s'afficheront sous forme de liste à puces élégante.</p>
                   </div>
                </div>
              )}
           </div>

           <footer className="modal-footer-premium">
              <button type="button" className="btn-modal-cancel" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-modal-save" disabled={saving}>
                 {saving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
                 <span>{mode === 'create' ? 'Créer le service' : 'Mettre à jour'}</span>
              </button>
           </footer>
        </form>
      </div>
    </div>
  );
}
